<template>
  <dashboard-template>
    <template slot="dashboard-header">
      <h6>{{ $t('products.products1') }}</h6>
    </template>
    <template slot="dashboard-body">
      <div v-if="!isError">
        <div v-if="!isLoading">
          <product-details-modal :productData="selectedProduct" :sources="sources"></product-details-modal>
          <div class="dashboard-content products-container">
            <div class="d-flex justify-content-between mb-3">
              <h6 class="mb-0">{{ $t('products.displayed_data') }}</h6>
              <img class="tooltip-icon icon-small mr-3" id="tooltip-products" v-bind:src="'/app/images/icons/info.svg'"/>
              <b-popover target="tooltip-products" variant="warning" triggers="hover" placement="bottomleft">
                <div v-html="$t('tooltips.products')"></div>
              </b-popover>
            </div>
            <multiselect
              class="mb-4 source-variables-multiselect"
              group-label="name"
              group-values="properties"
              label="propertyLabel"
              track-by="propertyName"
              v-model="selectedProductProperties"
              :allow-empty="false"
              :close-on-select="false"
              :deselect-label="''"
              :deselect-group-label="$t('products.deselect_group_label')"
              :group-select="true"
              :multiple="true"
              :options="productSources"
              :placeholder="$t('feeds.choose_variable')"
              :searchable="true"
              :select-label="''"
              :select-group-label="$t('products.select_group_label')"
              :selected-label="''">
              <template slot="tag" slot-scope="{ option, remove }">
                <span class="multiselect__tag">
                  <span>{{ option.propertyName }}</span>
                  <i class="multiselect__tag-icon" @click="remove(option)" aria-hidden="true" tabindex="1"></i>
                </span>
              </template>
              <span slot="noResult">{{ $t('global.multiselect_search_not_found') }}</span>
            </multiselect>
            <div class="d-flex align-items-center justify-content-between mb-4">
              <h6 class="mb-0">{{ $t('products.filter_view') }}</h6>
              <div class="d-flex">
                <button type="button" class="btn btn-medium btn-lower btn-gray-dark ml-4" @click="addFilterRule">
                  <plus-circle-icon></plus-circle-icon>
                  <span>{{ $t('feeds.add_filter') }}</span>
                </button>
                <button class="btn btn-medium btn-lower btn-orange-dark ml-4" @click="getProjectProducts">
                  <filter-icon></filter-icon>
                  <span>{{ $t('products.filter_button') }}</span>
                </button>
              </div>
            </div>
            <div v-for="(filterRule, index) in filterRules" v-bind:key="index">
              <div v-show="index > 0" class="custom-label condition-or-label bg-orange-light mb-3">{{ $t('conditions.or') }}</div>
              <div class="products-filtering-container d-flex justify-content-between py-3">
                <div>
                  <div v-for="(condition, index2) in filterRule.conditions" v-bind:key="index2">
                    <div class="d-flex align-items-center py-2">
                      <div v-if="index2 === 0" class="mr-3 pl-3">
                        <p class="mb-0">{{ $t('feeds.include') }}</p>
                      </div>
                      <div v-else class="custom-label condition-and-label bg-orange-light">{{ $t('conditions.and') }}</div>
                      <multiselect
                        label="propertyName"
                        track-by="propertyName"
                        v-model="condition.columnName"
                        :deselect-label="''"
                        :options="selectedProductProperties"
                        :placeholder="$t('global.column_name')"
                        :searchable="true"
                        :select-label="''"
                        :selected-label="''">
                        <template slot="option" slot-scope="props">
                          <span class="option__title">{{ props.option.propertyName }}</span>
                        </template>
                        <span slot="noResult">{{ $t('global.multiselect_search_not_found') }}</span>
                      </multiselect>
                      <multiselect
                        class="mx-4"
                        label="label"
                        track-by="label"
                        v-model="condition.conditionType"
                        :deselect-label="''"
                        :options="conditionTypes"
                        :placeholder="$t('global.condition_type')"
                        :searchable="false"
                        :select-label="''"
                        :selected-label="''">
                        <template slot="option" slot-scope="props">
                          <span class="option__title">{{ props.option.label }}</span>
                        </template>
                      </multiselect>
                      <input v-show="condition.conditionType.value !== 'empty' && condition.conditionType.value !== 'not empty'" v-model="condition.conditionString" class="form-control" name="conditionString" type="text" :placeholder="$t('global.condition')">
                      <button class="btn btn-tiny btn-lower btn-orange ml-4" @click="addCondition(filterRule)">
                        <plus-icon></plus-icon>
                      </button>
                      <button class="btn btn-tiny btn-lower btn-danger ml-4" v-show="index2 > 0" @click="removeCondition(filterRule.conditions, index2)">
                        <x-icon></x-icon>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-show="index > 0" class="mr-4 products-filter-remove-container">
                  <button class="btn btn-small btn-danger ml-3" @click="removeFilterRule(index)">
                    <x-circle-icon></x-circle-icon>
                    <span>{{ $t('feeds.delete_filter') }}</span>
                  </button>
                </div>
              </div>
            </div>
            <div class="mt-5 mb-4 d-flex justify-content-between align-items-center">
              <h6 class="mb-1">{{ $t('products.products_list') }} <span v-if="isFiltered">({{ productsCount}})</span></h6>
              <!--
              <download-csv :data="products">
                <button class="btn btn-medium btn-orange-dark ml-3" @click="downloadProductsAsCsv">
                  <download-icon></download-icon>
                  <span>Download as CSV</span>
                </button>
              </download-csv>
              <button class="btn btn-medium btn-orange-dark ml-3" @click="downloadProductsAsCsv">
                <download-icon></download-icon>
                <span>Download as CSV</span>
              </button>
              -->
            </div>
            <div v-if="products.length > 0">
              <products-pagination
                @onProductsPerPageChange="selectProductsPerPage"
                @onPaginateCallback="paginate"
                :page="paginationSettings.currentPage"
                :productsCount="productsCount"
                :productsPerPage="paginationSettings.perPage">
              </products-pagination>
              <div v-if="!isReloading">
                <table class="table table-striped table-bordered">
                  <thead class="thead-light">
                  <th></th>
                  <th v-for="(value, key) in selectedProductProperties" v-bind:key="key" @click="sortProducts(value.propertyName)">
                    <div class="d-flex justify-content-center align-items-center">
                      <div>{{ value.propertyName }}</div>
                      <div class="d-flex flex-column">
                        <chevron-up-icon></chevron-up-icon>
                        <chevron-down-icon></chevron-down-icon>
                      </div>
                    </div>
                  </th>
                  </thead>
                  <tbody>
                  <tr v-for="(product, index) in products" v-bind:key="index">
                    <td style="min-width: 5rem;">
                      <button class="btn btn-tiny-square btn-gray-dark p-0" @click="showProductDetails(product)">
                        <eye-icon></eye-icon>
                      </button>
                    </td>
                    <td v-for="(product_value, product_key) in selectedProductProperties" v-bind:key="product_key">
                      <p class="break-text">{{ product[product_value.propertyName] }}</p>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="reloading-spinner">
                <div class="spinner-border text-orange" role="status"></div>
              </div>
            </div>
            <div v-else class="alert alert-danger" role="alert">
              <p class="mb-0">{{ $t('products.no_products_found') }}</p>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="spinner-border text-orange" role="status"></div>
        </div>
      </div>
      <div v-else class="dashboard-content">
        <div class="d-flex align-items-center mb-5">
          <img class="icon" src="/app/images/icons/error.svg"/>
          <p class="text-danger mb-0"><strong>{{ $t('products.products_retrieve_error2') }}</strong></p>
        </div>
        <div v-if="sources.length > 0">
          <div v-if="!isErrorInternal" class="mb-5">
            <p>{{ $t('projects.project_update_called_previously') }}</p>
            <p>{{ $t('projects.project_update_not_called_previously') }}</p>
          </div>
          <div v-else>
            <p>{{ $t('products.products_retrieve_error') }}</p>
          </div>
          <button v-if="!isErrorInternal" type="button" class="btn btn-large btn-orange" @click="update">
            <refresh-cw-icon></refresh-cw-icon>
            <span>{{ $t('projects.project_show_update_now') }}</span>
          </button>
        </div>
        <div v-else>
          <div class="mb-5">
            <p>{{ $t('projects.no_source_files') }}</p>
            <p>{{ $t('projects.add_source') }}</p>
          </div>
          <router-link :to="{ name: 'source.create', params: { projectId: this.$route.params.id }}">
            <button type="button" class="btn btn-large btn-orange">
              <plus-circle-icon></plus-circle-icon>
              <span>{{ $t('projects.project_show_add_source') }}</span>
            </button>
          </router-link>
        </div>
      </div>
    </template>
  </dashboard-template>
</template>

<script>
import axios from 'axios'
import DashboardTemplate from '@/views/Dashboard'
import Multiselect from 'vue-multiselect'
import ProductDetailsModal from './Partials/ProductDetailsModal'
import ProductsPagination from './Partials/ProductsPagination'
import { ChevronDownIcon, ChevronUpIcon, EyeIcon, FilterIcon, PlusCircleIcon, PlusIcon, RefreshCwIcon, XCircleIcon, XIcon } from 'vue-feather-icons'
import products from '@/mixins/products'

export default {
  name: 'Products',

  data () {
    return {
      conditionTypes: [
        { label: this.$t('conditions.includes'), value: 'includes' },
        { label: this.$t('conditions.not_includes'), value: 'not includes' },
        { label: this.$t('conditions.regex'), value: 'regex' },
        { label: this.$t('conditions.lower_then'), value: 'lower then' },
        { label: this.$t('conditions.greater_then'), value: 'greater then' },
        { label: this.$t('conditions.value'), value: 'value' },
        { label: this.$t('conditions.empty'), value: 'empty' },
        { label: this.$t('conditions.not_equal'), value: 'not equal' },
        { label: this.$t('conditions.not_equal_column'), value: 'not equal column' },
        { label: this.$t('conditions.not_empty'), value: 'not empty' },
        { label: this.$t('conditions.in_list'), value: 'in list' }
      ],
      filterRules: [],
      isError: false,
      isErrorInternal: false,
      isFiltered: false,
      isLoading: true,
      isReloading: false,
      paginationSettings: {
        currentPage: 1,
        perPage: 10
      },
      productsCount: 0,
      productSources: [],
      products: [],
      selectedProduct: {},
      selectedProductProperties: [],
      sortColumn: '',
      sortOrder: 'asc',
      sources: this.$attrs.project.sources
    }
  },

  mixins: [products],

  created () {
    this.getProjectProducts()
  },

  methods: {
    getProjectProducts () {
      if (this.products.length > 0) this.isReloading = true
      else this.isLoading = true
      axios.post(process.env.VUE_APP_API_ENDPOINT + '/data/products/' + this.$route.params.id, {
        filterRules: this.filterRules.map(function (filterRule) {
          return {
            conditions: filterRule.conditions.map(function (condition) {
              return {
                columnName: condition.columnName.propertyName,
                conditionType: condition.conditionType.value,
                conditionString: condition.conditionString
              }
            })
          }
        }),
        orderBy: {
          sort: this.sortOrder,
          field: this.sortColumn
        },
        start: (this.paginationSettings.currentPage * this.paginationSettings.perPage) - this.paginationSettings.perPage,
        end: (this.paginationSettings.currentPage * this.paginationSettings.perPage)
      })
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            if (this.filterRules.length > 0) this.isFiltered = true
            this.products = response.data.products
            this.productsCount = response.data.length
            this.isReloading = false
            if (this.productsCount === 0) this.isLoading = false
            if (this.selectedProductProperties.length === 0) this.preselectProductProperties()
            else this.isLoading = false
          }
        })
        .catch((error) => {
          if (error.response.status === 400) {
            this.isError = true
          } else {
            if (this.productsCount > 0) {
              this.$bvToast.toast(this.$t('products.products_filtering_error_description'), {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('products.products_filtering_error'),
                variant: 'danger'
              })
            } else {
              this.isError = true
              this.isErrorInternal = true
            }
          }
        })
    },

    update () {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/feed/update/' + this.$route.params.id)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            window.dataLayer.push({ event: 'runFeedsUpdate' })
            this.$bvToast.toast(this.$t('feeds.feeds_updating_description'), {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('feeds.feeds_updating'),
              variant: 'info'
            })
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$bvToast.toast(this.$t('feeds.feeds_updating_error_description2'), {
              autoHideDelay: 3000,
              solid: true,
              title: this.$t('feeds.feeds_updating_error'),
              variant: 'danger'
            })
          }
        })
    },

    preselectProductProperties () {
      this.productSources = this.getProductSources(this.products[0])
      const lengths = this.productSources.map(source => source.properties.length)
      const productSourceIndex = lengths.indexOf(Math.max(...lengths))
      for (let i = 0; i < 5; i++) {
        this.selectedProductProperties.push(this.productSources[productSourceIndex].properties[i])
      }
      this.filterRules.push({ conditions: [{ columnName: '', conditionType: '', conditionString: '' }] })
      this.isLoading = false
    },

    selectProductsPerPage (value) {
      this.paginationSettings.perPage = value
      this.getProjectProducts()
    },

    paginate (value) {
      this.paginationSettings.currentPage = value
      this.getProjectProducts()
    },

    addFilterRule () {
      this.filterRules.push({ conditions: [{ columnName: '', conditionType: '', conditionString: '' }] })
    },

    removeFilterRule (index) {
      this.filterRules.splice(index, 1)
    },

    addCondition (filterRule) {
      filterRule.conditions.push({ columnName: '', conditionType: '', conditionString: '' })
    },

    removeCondition (conditions, index) {
      conditions.splice(index, 1)
    },

    showProductDetails (value) {
      this.selectedProduct = value
      this.$bvModal.show('productDetailsModal')
    },

    resetFilters () {
      this.filterRules = []
      this.filterRules.push({ conditions: [{ columnName: '', conditionType: '', conditionString: '' }] })
    },

    sortProducts (sortColumn) {
      if (this.sortOrder === 'asc') this.sortOrder = 'desc'
      else this.sortOrder = 'asc'
      this.sortColumn = sortColumn
      this.getProjectProducts()
    },

    downloadProductsAsCsv () {
      //
    }
  },

  watch: {
    productsPerPage (newValue) {
      if ((this.page * newValue) > this.productsCount) {
        this.page = 1
      }
    }
  },

  components: { ChevronDownIcon, ChevronUpIcon, DashboardTemplate, EyeIcon, FilterIcon, Multiselect, PlusCircleIcon, PlusIcon, ProductDetailsModal, ProductsPagination, RefreshCwIcon, XIcon, XCircleIcon }
}
</script>
